import React, { useState } from 'react';
import CreateTableForm from './CreateTableForm';
import ColumnConfig from './ColumnConfig';
import config from '../config.json';
import LanguageSelection from './LanguageSelection';

type ToastType = 'success' | 'error' | 'warning';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

export interface RemoteCredentials {
  host: string;
  port: string;
  database: string;
  username: string;
  password: string;
}

export interface RemoteState {
  credentials: RemoteCredentials;
  connected: boolean;
  remoteTables: string[];
  selectedRemote: Set<string>;
  newTables: string[];
  selectedNew: Set<string>;
}

interface Props {
  remoteState: RemoteState;
  setRemoteState: React.Dispatch<React.SetStateAction<RemoteState>>;
}

const toastStyles: Record<ToastType, React.CSSProperties> = {
  success: { background: 'linear-gradient(135deg, #28a745, #20c997)' },
  error:   { background: 'linear-gradient(135deg, #dc3545, #c82333)' },
  warning: { background: 'linear-gradient(135deg, #ffc107, #e0a800)' },
};

const toastIcons: Record<ToastType, string> = {
  success: '✔',
  error:   '✖',
  warning: '⚠',
};

const ITEMS_PER_PAGE = 5;
const REMOTE_ENDPOINT = `${config.ENDPOINTS.REMOTE_CONNECTION}`;

const Pagination: React.FC<{ current: number; total: number; onChange: (p: number) => void }> = ({ current, total, onChange }) => {
  if (total <= 1) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 18px 14px', fontSize: '13px', color: '#888' }}>
      <span>{current} / {total}</span>
      <div style={{ display: 'flex', gap: '4px' }}>
        <button className="page-btn" onClick={() => onChange(current - 1)} disabled={current === 1}>‹</button>
        {Array.from({ length: total }, (_, i) => i + 1).map(p => (
          <button key={p} className={`page-btn ${p === current ? 'active' : ''}`} onClick={() => onChange(p)}>{p}</button>
        ))}
        <button className="page-btn" onClick={() => onChange(current + 1)} disabled={current === total}>›</button>
      </div>
    </div>
  );
};

const RemoteContent: React.FC<Props> = ({ remoteState, setRemoteState }) => {

  const [connecting, setConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [tableName, setTableName] = useState('');
  const [columnCount, setColumnCount] = useState(1);
  const [columns, setColumns] = useState<any[]>([]);
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [foreignTables, setForeignTables] = useState<string[]>([]);
  const [showModals, setShowModals] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [confirmTable, setConfirmTable] = useState<{ name: string; isNew: boolean } | null>(null);
  const [pageRemote, setPageRemote] = useState(1);
  const [pageNew, setPageNew] = useState(1);

  const { credentials, connected, remoteTables, selectedRemote, newTables, selectedNew } = remoteState;

  const setCredentials = (updater: (prev: RemoteCredentials) => RemoteCredentials) => {
    setRemoteState(prev => ({ ...prev, credentials: updater(prev.credentials) }));
  };
  const setRemoteTables = (fn: (p: string[]) => string[]) => setRemoteState(prev => ({ ...prev, remoteTables: fn(prev.remoteTables) }));
  const setSelectedRemote = (fn: (p: Set<string>) => Set<string>) => setRemoteState(prev => ({ ...prev, selectedRemote: fn(prev.selectedRemote) }));
  const setNewTables = (fn: (p: string[]) => string[]) => setRemoteState(prev => ({ ...prev, newTables: fn(prev.newTables) }));
  const setSelectedNew = (fn: (p: Set<string>) => Set<string>) => setRemoteState(prev => ({ ...prev, selectedNew: fn(prev.selectedNew) }));

  const showToast = (message: string, type: ToastType = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  };

  const callRemote = async (action: string, extra: object = {}) => {
    const payload: any = { action, database: credentials.database, username: credentials.username, password: credentials.password, ...extra };
    if (credentials.host) payload.host = credentials.host;
    if (credentials.port) payload.port = parseInt(credentials.port);
    const res = await fetch(REMOTE_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    return res.json();
  };

  const handleConnect = async () => {
    if (!credentials.database || !credentials.username) { setConnectionError('Database y Username son obligatorios.'); return; }
    setConnecting(true); setConnectionError(null);
    try {
      const data = await callRemote('listTables');
      if (data.success) {
        setRemoteState(prev => ({ ...prev, connected: true, remoteTables: data.tables || [], selectedRemote: new Set(data.tables || []) }));
        showToast('Conexión exitosa.', 'success');
      } else { setConnectionError(data.message || 'Error de conexión.'); }
    } catch { setConnectionError('No se pudo contactar al servidor.'); }
    setConnecting(false);
  };

  const toggleRemote = (name: string) => { setSelectedRemote(prev => { const next = new Set(prev); next.has(name) ? next.delete(name) : next.add(name); return next; }); };
  const toggleAllRemote = () => { setSelectedRemote(() => selectedRemote.size === remoteTables.length ? new Set() : new Set(remoteTables)); };
  const toggleNew = (name: string) => { setSelectedNew(prev => { const next = new Set(prev); next.has(name) ? next.delete(name) : next.add(name); return next; }); };
  const toggleAllNew = () => { setSelectedNew(() => selectedNew.size === newTables.length ? new Set() : new Set(newTables)); };

  const totalSelected = selectedRemote.size + selectedNew.size;

  const handleCreateTable = () => {
    const idColumn = { name: 'id', type: 'Número', isNullable: false, isPrimaryKey: true, isUnique: true, isForeignKey: false, relatedTable: '', relatedColumn: '', onDelete: 'RESTRICT', onUpdate: 'RESTRICT' };
    setColumns([idColumn, ...Array.from({ length: columnCount }, () => ({ name: '', type: 'Texto', isNullable: false, isPrimaryKey: false, isUnique: false, isForeignKey: false, relatedTable: '', relatedColumn: '', onDelete: 'RESTRICT', onUpdate: 'RESTRICT' }))]);
    setShowColumnConfig(true);
    setForeignTables([...remoteTables, ...newTables]);
  };

  const handleColumnChange = (index: number, key: string, value: any) => { const updated = [...columns]; updated[index][key] = value; setColumns(updated); };
  const addNewColumn = () => { setColumns([...columns, { name: '', type: 'Texto', isNullable: false, isPrimaryKey: false, isUnique: false, isForeignKey: false, relatedTable: '', relatedColumn: '', onDelete: 'RESTRICT', onUpdate: 'RESTRICT' }]); };
  const removeLastColumn = () => { if (columns.length > 1) setColumns(columns.slice(0, -1)); };
  const handleNewTable = () => { setTableName(''); setColumnCount(1); setColumns([]); setShowColumnConfig(false); };

  const handleSubmit = async () => {
    if (!tableName || columns.length === 0) { showToast('Ingresa un nombre de tabla y al menos una columna.', 'warning'); return; }
    const data = await callRemote('createTable', { tableName, columns });
    if (data.success) { showToast(`Tabla '${tableName}' creada con éxito.`, 'success'); setNewTables(prev => [...prev, tableName]); setSelectedNew(prev => new Set([...prev, tableName])); handleNewTable(); }
    else { showToast(data.message || 'No se pudo crear la tabla.', 'error'); }
  };

  const askDrop = (name: string, isNew: boolean) => setConfirmTable({ name, isNew });

  const confirmDrop = async () => {
    if (!confirmTable) return;
    const { name, isNew } = confirmTable; setConfirmTable(null);
    const data = await callRemote('dropTable', { tableName: name });
    if (data.success) {
      showToast(`Tabla '${name}' eliminada.`, 'success');
      if (isNew) { setNewTables(prev => prev.filter(t => t !== name)); setSelectedNew(prev => { const n = new Set(prev); n.delete(name); return n; }); }
      else { setRemoteTables(prev => prev.filter(t => t !== name)); setSelectedRemote(prev => { const n = new Set(prev); n.delete(name); return n; }); }
    } else { showToast(data.message || 'Error al eliminar.', 'error'); }
  };

  const callBackendForGeneration = async (framework = 'Angular') => {
    try {
      const selectedTables = [...Array.from(selectedRemote), ...Array.from(selectedNew)];
      if (selectedTables.length === 0) { showToast('Selecciona al menos una tabla.', 'warning'); return; }
      const allTablesWithColumns = await Promise.all(selectedTables.map(async (table) => { const colData = await callRemote('listColumns', { tableName: table }); return { tableName: table, columns: colData.columns || [] }; }));
      const response = await fetch(`${config.ENDPOINTS.CALLER_LANGUAGE}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ framework, tables: allTablesWithColumns, databaseName: credentials.database }) });
      const result = await response.json();
      console.log('Respuesta de CallerLenguage.php:', result);
    } catch (error) { console.error('Error al generar el código:', error); }
  };

  const handleDisconnect = () => {
    setRemoteState({ credentials: { host: '', port: '', database: '', username: '', password: '' }, connected: false, remoteTables: [], selectedRemote: new Set(), newTables: [], selectedNew: new Set() });
    setShowColumnConfig(false);
  };

  const totalPagesRemote = Math.ceil(remoteTables.length / ITEMS_PER_PAGE);
  const paginatedRemote = remoteTables.slice((pageRemote - 1) * ITEMS_PER_PAGE, pageRemote * ITEMS_PER_PAGE);
  const totalPagesNew = Math.ceil(newTables.length / ITEMS_PER_PAGE);
  const paginatedNew = newTables.slice((pageNew - 1) * ITEMS_PER_PAGE, pageNew * ITEMS_PER_PAGE);

  // ── Estilo compartido para inputs del formulario ──────────────────────────
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px', fontSize: '14px',
    border: '1.5px solid #e8e8e8', borderRadius: '8px',
    outline: 'none', color: '#333', background: '#fafafa',
    transition: 'all 0.15s ease', boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: '11px', fontWeight: 600,
    color: '#666', marginBottom: '6px',
    textTransform: 'uppercase', letterSpacing: '0.06em',
  };

  // ── Estilo base compartido para botones de acción (igual que Form Wizard) ──
  const actionBtn: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '7px 16px', fontSize: '13px', fontWeight: 500,
    borderRadius: '8px', border: 'none', cursor: 'pointer',
    transition: 'all 0.15s ease', whiteSpace: 'nowrap',
  };

  // ── Inline styles para el header de tablas (evita conflictos con Bootstrap/AdminLTE) ──
  const tableCardHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    gap: '8px',
    borderBottom: '1px solid rgba(0,0,0,0.125)',
    background: '#f8f9fa',
  };
  const tableCardHeaderTitleStyle: React.CSSProperties = {
    margin: 0,
    flex: 1,
    minWidth: 0,
    maxWidth: 'calc(100% - 160px)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '14px',
  };
  const tableCardHeaderCheckStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexShrink: 0,
    margin: 0,
    padding: 0,
    minWidth: '140px',
    justifyContent: 'flex-end',
  };
  const checkInputStyle: React.CSSProperties = {
    margin: 0,
    flexShrink: 0,
    position: 'static',
    float: 'none',
  };
  const checkLabelStyle: React.CSSProperties = {
    margin: 0,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    paddingLeft: 0,
  };

  return (
    <div>
      {/* TOASTS */}
    <div style={{ position: 'fixed', top: '70px', right: '20px', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {toasts.map(toast => (
          <div key={toast.id} style={{ ...toastStyles[toast.type], color: toast.type === 'warning' ? '#333' : '#fff', padding: '14px 20px', borderRadius: '10px', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: '10px', minWidth: '280px', maxWidth: '380px', fontSize: '14px', fontWeight: 500, animation: 'slideIn 0.3s ease' }}>
            <span style={{ fontSize: '18px' }}>{toastIcons[toast.type]}</span>
            <span>{toast.message}</span>
          </div>
        ))}
      </div>

      {/* MODAL DE CONFIRMACIÓN */}
      {confirmTable && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '36px 36px 36px', maxWidth: '400px', width: '100%', minHeight: '257px', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', textAlign: 'center', animation: 'popIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗑️</div>
            <h5 style={{ fontSize: '20px', fontWeight: 700, color: '#1a1a1a', marginBottom: '10px' }}>¿Eliminar tabla?</h5>
            <p style={{ fontSize: '14px', color: '#555', lineHeight: 1.6, marginBottom: '8px' }}>Estás a punto de eliminar <strong style={{ color: '#1a1a1a' }}>{confirmTable.name}</strong>.</p>
            <span style={{ display: 'block', fontSize: '13px', color: '#dc3545', marginBottom: '20px' }}>Esta acción no se puede deshacer.</span>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button className="modal-btn modal-btn-cancel" onClick={() => setConfirmTable(null)}>Cancelar</button>
              <button className="modal-btn modal-btn-danger" onClick={confirmDrop}>Sí, eliminar</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideIn { from { opacity: 0; transform: translateX(60px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes popIn { from { opacity: 0; transform: scale(0.88); } to { opacity: 1; transform: scale(1); } }
        .remote-input:focus { border-color: #1a7fd4 !important; background: #fff !important; box-shadow: 0 0 0 3px rgba(26,127,212,0.1) !important; }
        .table-row-check { display: flex; justify-content: space-between; align-items: center; padding: 14px 20px; margin: 6px 12px; border-radius: 10px; background: #fff; border: 1px solid #e9ecef; transition: all 0.18s ease; animation: fadeInUp 0.25s ease both; }
        .table-row-check:hover { background: #f0f7ff; border-color: #b8d4f5; box-shadow: 0 2px 8px rgba(13,110,253,0.08); transform: translateY(-1px); }
        .table-icon { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 8px; background: linear-gradient(135deg, #e8f0fe, #d0e4ff); font-size: 15px; flex-shrink: 0; }
        .table-name-label { font-weight: 600; font-size: 14px; color: #212529; letter-spacing: 0.01em; }
        .tables-list-wrapper { padding: 8px 0 4px; }
        @media (max-width: 576px) {
          .table-row-check { padding: 10px 12px; margin: 4px 6px; }
          .remote-card-header { padding: 14px 16px !important; }
          .remote-card-body { padding: 16px !important; }
        }
        .page-btn { width: 30px; height: 30px; border-radius: 7px; border: 1px solid #e0e0e0; background: #fff; color: #444; font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.15s ease; display: inline-flex; align-items: center; justify-content: center; }
        .page-btn:hover:not(:disabled) { background: #f0f7ff; border-color: #b8d4f5; }
        .page-btn.active { background: #1a7fd4; color: #fff; border-color: #1a7fd4; }
        .page-btn:disabled { opacity: 0.35; cursor: not-allowed; }
        .modal-btn { padding: 11px 28px; font-size: 14px; font-weight: 600; border-radius: 10px; border: none; cursor: pointer; min-width: 120px; transition: all 0.15s ease; }
        .modal-btn-cancel { background: #f5f5f5; color: #444; border: 1px solid #ddd !important; }
        .modal-btn-cancel:hover { background: #eaeaea; }
        .modal-btn-danger { background: linear-gradient(135deg, #e53935, #c62828); color: #fff; box-shadow: 0 4px 12px rgba(220,53,69,0.35); }
        .modal-btn-danger:hover { background: linear-gradient(135deg, #c62828, #b71c1c); transform: translateY(-1px); }
        .btn-connect-remote { width: 100%; padding: 13px; background: linear-gradient(135deg, #1a7fd4, #0d5ba8); color: #fff; border: none; border-radius: 10px; font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.15s ease; display: flex; align-items: center; justify-content: center; gap: 8px; box-shadow: 0 4px 14px rgba(26,127,212,0.35); margin-top: 8px; }
        .btn-connect-remote:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(26,127,212,0.45); }
        .btn-connect-remote:disabled { opacity: 0.7; cursor: not-allowed; }
        .remote-card-header { display: flex; align-items: center; gap: 14px; padding: 20px 24px !important; border-bottom: 1px solid #f0f0f0; }
        .remote-card-body { padding: 24px 24px 28px !important; }
        .remote-header-text { display: flex; flex-direction: column; gap: 2px; }
        .remote-header-text h3 { margin: 0; font-size: 16px; font-weight: 700; white-space: nowrap; }
        .remote-header-text p { margin: 2px 0 0; font-size: 12px; color: #aaa; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 220px; }
        .connected-bar { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; margin-bottom: 12px; padding: 0 4px; flex-wrap: wrap; }
        .connected-bar span { font-size: 13px; word-break: break-all; }
        .action-btn:hover { filter: brightness(0.92); transform: translateY(-1px); box-shadow: 0 3px 10px rgba(0,0,0,0.12); }
        .action-btn:active { transform: translateY(0); }
        @media (max-width: 576px) {
          .remote-card-body { padding: 16px !important; }
          .remote-card-header { padding: 14px 16px !important; }
          .table-row-check { padding: 10px 10px; margin: 4px 6px; }
          .table-name-label { font-size: 13px; }
        }
      `}</style>

      {/* FORMULARIO DE CONEXIÓN */}
      {!connected && (
        <div className="card">
          <div className="card-header remote-card-header">
            <div style={{ width: '42px', height: '42px', background: 'linear-gradient(135deg, #1a7fd4, #0d5ba8)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0, boxShadow: '0 4px 10px rgba(26,127,212,0.3)' }}>
              🔌
            </div>
            <div className="remote-header-text">
              <h3 className="card-title">Conexión remota</h3>
              <p>Conecta a tu base de datos MySQL</p>
            </div>
          </div>

          <div className="card-body remote-card-body">
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '20px', marginTop: '12px' }}>
              <div>
                <label style={labelStyle}>Server / Host <span style={{ textTransform: 'none', fontWeight: 400, color: '#aaa', letterSpacing: 0, fontSize: '11px' }}>(opcional)</span></label>
                <input className="remote-input" style={inputStyle} type="text" value={credentials.host} placeholder="localhost"
                  onChange={e => setCredentials(p => ({ ...p, host: e.target.value }))} />
              </div>
              <div>
                <label style={labelStyle}>Puerto <span style={{ textTransform: 'none', fontWeight: 400, color: '#aaa', letterSpacing: 0, fontSize: '11px' }}>(opcional)</span></label>
                <input className="remote-input" style={inputStyle} type="text" value={credentials.port} placeholder="3306"
                  onChange={e => setCredentials(p => ({ ...p, port: e.target.value.replace(/[^0-9]/g, '') }))} />
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Database</label>
              <input className="remote-input" style={inputStyle} type="text" value={credentials.database} placeholder="nombre_base_de_datos"
                onChange={e => setCredentials(p => ({ ...p, database: e.target.value }))} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={labelStyle}>Username</label>
                <input className="remote-input" style={inputStyle} type="text" value={credentials.username} placeholder="root"
                  onChange={e => setCredentials(p => ({ ...p, username: e.target.value }))} />
              </div>
              <div>
                <label style={labelStyle}>Password</label>
                <input className="remote-input" style={inputStyle} type="password" value={credentials.password} placeholder="••••••••"
                  onChange={e => setCredentials(p => ({ ...p, password: e.target.value }))} />
              </div>
            </div>

            {connectionError && <div className="alert alert-danger mt-2 mb-3">{connectionError}</div>}

            <button className="btn-connect-remote" onClick={handleConnect} disabled={connecting}>
              {connecting ? '⏳ Conectando...' : '🔗 Conectar'}
            </button>
          </div>
        </div>
      )}

      {/* VISTA CONECTADA */}
      {connected && (
        <div>
          <div className="connected-bar">
            <span className="text-success fw-bold">
              ✓ Conectado a <strong>{credentials.database}</strong>
              {credentials.host ? ` — ${credentials.host}${credentials.port ? ':' + credentials.port : ''}` : ''}
            </span>
            <button className="btn btn-sm btn-outline-secondary" style={{flexShrink: 0}} onClick={handleDisconnect}>Desconectar</button>
          </div>

          {/* CREAR TABLA */}
          <div className="card mb-3">
            <div className="card-header"><h3 className="card-title">Crear nueva tabla</h3></div>
            <div className="card-body">
              {!showColumnConfig && (
                <CreateTableForm tableName={tableName} columnCount={columnCount} setTableName={setTableName} setColumnCount={setColumnCount} handleCreateTable={handleCreateTable} handleNext={() => setShowModals(true)} hasTables={totalSelected > 0} />
              )}
              {showColumnConfig && (
                <div className="mt-4">
                  <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#333', marginBottom: '16px' }}>
                    Configurar columnas
                  </h4>
                  <form>
                    {columns.map((col, index) => (
                      <ColumnConfig key={index} index={index} column={col} handleColumnChange={handleColumnChange} foreignTables={foreignTables} />
                    ))}

                    {/* Botones de acción — mismo estilo que Form Wizard */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '20px' }}>
                      <button type="button" className="action-btn"
                        style={{ ...actionBtn, background: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7' }}
                        onClick={addNewColumn}>
                        ＋ Agregar columna
                      </button>
                      <button type="button" className="action-btn"
                        style={{ ...actionBtn, background: '#fce4ec', color: '#c62828', border: '1px solid #ef9a9a' }}
                        onClick={removeLastColumn}>
                        － Quitar última
                      </button>
                      <button type="button" className="action-btn"
                        style={{ ...actionBtn, background: '#1a7fd4', color: '#fff', border: '1px solid #1569b5' }}
                        onClick={handleSubmit}>
                        ✔ Crear tabla
                      </button>
                      <button type="button" className="action-btn"
                        style={{ ...actionBtn, background: '#f5f5f5', color: '#555', border: '1px solid #ddd' }}
                        onClick={handleNewTable}>
                        ← Volver
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>

          {/* TABLAS REMOTAS */}
          {remoteTables.length > 0 && (
            <div className="card mb-3">
              <div style={tableCardHeaderStyle}>
                <h5 className="mb-0" style={tableCardHeaderTitleStyle}>
                  Tablas en <strong>{credentials.database}</strong>
                  <span className="badge bg-secondary ms-2">{remoteTables.length}</span>
                </h5>
                <div style={tableCardHeaderCheckStyle}>
                  <input type="checkbox" className="form-check-input" id="selectAllRemote" style={checkInputStyle} checked={selectedRemote.size === remoteTables.length} onChange={toggleAllRemote} />
                  <label className="form-check-label" htmlFor="selectAllRemote" style={checkLabelStyle}>Seleccionar todas</label>
                </div>
              </div>
              <div className="tables-list-wrapper">
                {paginatedRemote.map((t, i) => (
                  <div key={t} className="table-row-check" style={{ animationDelay: `${i * 0.05}s` }}>
                    <div className="d-flex align-items-center gap-3">
                      <input type="checkbox" className="form-check-input" checked={selectedRemote.has(t)} onChange={() => toggleRemote(t)} />
                      <span className="table-icon">🗄️</span>
                      <span className="table-name-label">{t}</span>
                    </div>
                    <button className="btn btn-sm btn-outline-danger" style={{ borderRadius: '8px', padding: '4px 12px' }} onClick={() => askDrop(t, false)}>
                      <i className="fas fa-times me-1"></i>Eliminar
                    </button>
                  </div>
                ))}
              </div>
              <Pagination current={pageRemote} total={totalPagesRemote} onChange={setPageRemote} />
            </div>
          )}

          {/* TABLAS NUEVAS */}
          {newTables.length > 0 && (
            <div className="card mb-3">
              <div style={tableCardHeaderStyle}>
                <h5 className="mb-0" style={tableCardHeaderTitleStyle}>
                  Tablas creadas en esta sesión
                  <span className="badge bg-success ms-2">{newTables.length}</span>
                </h5>
                <div style={tableCardHeaderCheckStyle}>
                  <input type="checkbox" className="form-check-input" id="selectAllNew" style={checkInputStyle} checked={selectedNew.size === newTables.length} onChange={toggleAllNew} />
                  <label className="form-check-label" htmlFor="selectAllNew" style={checkLabelStyle}>Seleccionar todas</label>
                </div>
              </div>
              <div className="tables-list-wrapper">
                {paginatedNew.map((t, i) => (
                  <div key={t} className="table-row-check" style={{ animationDelay: `${i * 0.05}s` }}>
                    <div className="d-flex align-items-center gap-3">
                      <input type="checkbox" className="form-check-input" checked={selectedNew.has(t)} onChange={() => toggleNew(t)} />
                      <span className="table-icon">✨</span>
                      <span className="table-name-label">{t}</span>
                    </div>
                    <button className="btn btn-sm btn-outline-danger" style={{ borderRadius: '8px', padding: '4px 12px' }} onClick={() => askDrop(t, true)}>
                      <i className="fas fa-times me-1"></i>Eliminar
                    </button>
                  </div>
                ))}
              </div>
              <Pagination current={pageNew} total={totalPagesNew} onChange={setPageNew} />
            </div>
          )}

          <LanguageSelection sessionUUID={credentials.database} show={showModals} onClose={() => setShowModals(false)} tableName={tableName} columns={columns} onGenerate={callBackendForGeneration} />
        </div>
      )}
    </div>
  );
};

export default RemoteContent;